import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';

interface VisibleSectionsPristineContextType {
  isPristine: boolean;
  setIsPristine: (isPristine: boolean) => void;
}

const visibleSectionsPristineContextInitialValue = {
  isPristine: true,
  setIsPristine: () => void 0,
};

export const VisibleSectionsPristineContext =
  createContext<VisibleSectionsPristineContextType>(
    visibleSectionsPristineContextInitialValue
  );

interface Props {
  children: ReactNode;
}

/**
 * This context provider contains a slice of VisibleSectionsProvider - isPristine
 * isPristine field signifies whether there is only one visible section loaded
 * which is important for SEO purposes as we adjust headline levels based on that
 * to support ranking of individual sections independently.
 * Pristine sections also skip virtualization as they receive SSRed HTML which
 * only needs to be hydrated and we want to prevent unmounting those sections.
 * The reason behind splitting one context into 2 is that
 * useVisibleSectionsContext changes every time we move to another section and that causes
 * any component that consumes this context re-render.
 * However by consuming useVisibleSectionsIsPristine, this flag only changes once
 * when a second section is loaded.
 */
export const VisibleSectionsPristineProvider = ({ children }: Props) => {
  const [isPristine, setIsPristine] = useState(
    visibleSectionsPristineContextInitialValue.isPristine
  );

  return (
    <VisibleSectionsPristineContext.Provider
      value={{
        isPristine,
        setIsPristine,
      }}
    >
      {children}
    </VisibleSectionsPristineContext.Provider>
  );
};

export const useVisibleSectionsPristineContext =
  (): VisibleSectionsPristineContextType =>
    useContext(VisibleSectionsPristineContext);

export const useVisibleSectionsIsPristine = () => {
  const { isPristine } = useContext(VisibleSectionsPristineContext);
  return isPristine;
};
